/**
 * Tabs Component
 * 
 * A versatile tabs component for organizing content into separate views
 * that are displayed one at a time.
 */
import { useState } from 'react'
import { cn } from '../../utils/tailwind-utils'

interface Tab {
  /**
   * Unique identifier for the tab
   */
  id: string
  
  /**
   * Label to display in the tab
   */
  label: React.ReactNode
  
  /**
   * Content to display when tab is active
   */
  content: React.ReactNode
  
  /**
   * Whether the tab is disabled
   */
  disabled?: boolean
  
  /**
   * Optional icon to display before the label
   */
  icon?: React.ReactNode
}

interface TabsProps {
  /**
   * Array of tab objects
   */
  tabs: Tab[]
  
  /**
   * ID of the initially active tab
   */
  defaultActiveTab?: string
  
  /**
   * ID of the controlled active tab
   */
  activeTab?: string
  
  /**
   * Function called when active tab changes
   */
  onTabChange?: (tabId: string) => void
  
  /**
   * Orientation of the tabs
   * @default "horizontal"
   */
  orientation?: 'horizontal' | 'vertical'
  
  /**
   * Visual variant of the tabs
   * @default "default"
   */
  variant?: 'default' | 'pills' | 'underlined'
  
  /**
   * Additional CSS classes for the tabs container
   */
  className?: string
  
  /**
   * Additional CSS classes for the tab list
   */
  tabListClassName?: string
  
  /**
   * Additional CSS classes for the tab content
   */
  tabContentClassName?: string
}

/**
 * Tabs component for organizing content into separate views
 * 
 * @example
 * // Basic usage
 * <Tabs
 *   tabs={[
 *     { id: 'tab1', label: 'Tab 1', content: <div>Content for Tab 1</div> },
 *     { id: 'tab2', label: 'Tab 2', content: <div>Content for Tab 2</div> }
 *   ]}
 * />
 * 
 * @example
 * // With controlled state
 * <Tabs
 *   tabs={tabs}
 *   activeTab={activeTab}
 *   onTabChange={setActiveTab}
 *   variant="pills"
 * />
 * 
 * @example
 * // Vertical orientation
 * <Tabs
 *   tabs={tabs}
 *   orientation="vertical"
 *   variant="underlined"
 * />
 */
function Tabs({
  tabs,
  defaultActiveTab,
  activeTab: controlledActiveTab,
  onTabChange,
  orientation = 'horizontal',
  variant = 'default',
  className,
  tabListClassName,
  tabContentClassName,
}: TabsProps) {
  // Use controlled or uncontrolled state
  const [internalActiveTab, setInternalActiveTab] = useState(
    defaultActiveTab || (tabs.length > 0 ? tabs[0].id : '')
  )
  
  // Determine if component is controlled or uncontrolled
  const isControlled = controlledActiveTab !== undefined
  const activeTabId = isControlled ? controlledActiveTab : internalActiveTab
  
  // Handle tab click
  const handleTabClick = (tabId: string) => {
    if (!isControlled) {
      setInternalActiveTab(tabId)
    }
    
    if (onTabChange) {
      onTabChange(tabId)
    }
  }
  
  // Variant styles
  const getTabStyles = (isActive: boolean, isDisabled: boolean) => {
    const baseStyles = 'flex items-center px-4 py-2 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2'
    
    const variantStyles = {
      default: cn(
        'border-b-2',
        isActive
          ? 'border-primary text-primary'
          : 'border-transparent text-muted-foreground hover:border-border hover:text-foreground',
        isDisabled && 'opacity-50 cursor-not-allowed hover:border-transparent hover:text-muted-foreground'
      ),
      pills: cn(
        'rounded-md',
        isActive
          ? 'bg-primary text-primary-foreground'
          : 'bg-transparent text-muted-foreground hover:bg-muted hover:text-foreground',
        isDisabled && 'opacity-50 cursor-not-allowed hover:bg-transparent hover:text-muted-foreground'
      ),
      underlined: cn(
        'border-b-2',
        isActive
          ? 'border-primary text-primary'
          : 'border-transparent text-muted-foreground hover:text-foreground',
        isDisabled && 'opacity-50 cursor-not-allowed hover:text-muted-foreground'
      ),
    }
    
    return cn(baseStyles, variantStyles[variant])
  }
  
  // Orientation styles
  const orientationStyles = {
    horizontal: {
      tabList: 'flex space-x-1 border-b border-border',
      tabContent: 'pt-4',
    },
    vertical: {
      tabList: 'flex flex-col space-y-1 border-r border-border',
      tabContent: 'pl-4',
    },
  }
  
  return (
    <div 
      className={cn(
        'w-full',
        orientation === 'vertical' && 'flex',
        className
      )}
      role="tablist"
      aria-orientation={orientation}
    >
      {/* Tab list */}
      <div 
        className={cn(
          orientationStyles[orientation].tabList,
          tabListClassName
        )}
      >
        {tabs.map((tab) => (
          <button
            key={tab.id}
            role="tab"
            aria-selected={activeTabId === tab.id}
            aria-controls={`panel-${tab.id}`}
            id={`tab-${tab.id}`}
            tabIndex={activeTabId === tab.id ? 0 : -1}
            className={getTabStyles(activeTabId === tab.id, !!tab.disabled)}
            onClick={() => !tab.disabled && handleTabClick(tab.id)}
            disabled={tab.disabled}
          >
            {tab.icon && <span className="mr-2">{tab.icon}</span>}
            {tab.label}
          </button>
        ))}
      </div>
      
      {/* Tab content */}
      <div 
        className={cn(
          'flex-1',
          orientationStyles[orientation].tabContent,
          tabContentClassName
        )}
      >
        {tabs.map((tab) => (
          <div
            key={tab.id}
            role="tabpanel"
            id={`panel-${tab.id}`}
            aria-labelledby={`tab-${tab.id}`}
            tabIndex={0}
            hidden={activeTabId !== tab.id}
            className={activeTabId === tab.id ? 'animate-in fade-in-0' : ''}
          >
            {tab.content}
          </div>
        ))}
      </div>
    </div>
  )
}

export default Tabs
